import {
  Box,
  Flex,
  Icon,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Scrollbar,
  Spinner,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { SuccessIcon, SyncIcon } from '@tonic-one/react-icons';
import { useTranslation } from 'react-i18next';
import { PolicyTypeEnum } from '../utils/constants';
import {
  checkManualScanCanceled,
  checkManualScanCancelling,
  checkManualScanCompleted,
  checkManualScanFailed,
  checkManualScanFinished,
  checkManualScanPending,
} from '../utils/fns';
import { usePermissionStore } from '../../../models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import {
  OverAllPolicyType,
  PolicyItemInTable,
  PolicyListModalName,
  PolicyListModalState,
} from '../utils/types';
import { useContext, useState } from 'react';
import { OverAllPolicyTypeContext } from '../OverAllPolicyList/context/OverAllPolicyTypeContext';
import { PolicyApi, ReportApi } from '@Apis';
import dayjs from 'dayjs';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { getUtcTime } from '@Utils/time';
import { ConsoleType } from '@Utils/enums';

interface Props {
  // policy: {
  //   taskState?: string;
  //   taskParam?: string;
  //   policyType: PolicyTypeEnum;
  //   scanHistory: any[];
  //   [key: string]: any;
  // };
  policy: PolicyItemInTable;
  setModal: ({ name, policy, scanHistory }: PolicyListModalState) => void;
}

export interface SeeDetailsProps {
  downloadLinkId: string;
  generatedReportGuid: string;
}

export const PolicyManualScanCell = ({ policy, setModal }: Props) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [item, setItem] = useState(policy.scanHistory?.[0] || null);
  const [item2, setItem2] = useState(policy.scanHistory?.[1] || null);
  const onClick = async () => {
    // Check if items exist before making API calls
    if (!item && !item2) {
      return;
    }

    try {
      if (item) {
        const scanHistory = (
          await PolicyApi.compileScanHistory(
            item['parentTaskId'],
            policy.policyUuid
          )
        ).data;
        setItem({
          ...item,
          ...scanHistory,
        });
      }

      if (item2) {
        const scanHistory2 = (
          await PolicyApi.compileScanHistory(
            item2['parentTaskId'],
            policy.policyUuid
          )
        ).data;
        setItem2({
          ...item2,
          ...scanHistory2,
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const { taskState, scanHistory, policyType } = policy;
  if (!taskState) {
    return <>-</>;
  }
  if (checkManualScanPending(taskState)) {
    if (checkManualScanCancelling(taskState)) {
      return (
        <Flex alignItems="center">
          <Icon as={SyncIcon} />
          <Text>{t('policy.status.stopping')}</Text>
        </Flex>
      );
    }
    return (
      <Flex alignItems="center">
        <Icon as={SyncIcon} />
        <Link
          ml="4px"
          // disabled={true}
          onClick={() => {
            setModal({
              name: PolicyListModalName.Scanning,
              policy,
            });
          }}
        >
          {t('policy.status.scanning')}
        </Link>
      </Flex>
    );
  } else {
    if (!checkManualScanFinished(taskState)) {
      return <></>;
    }
    // const parameters = item.taskParameter ? JSON.parse(item.taskParameter) : {};

    return (
      <Popover arrow={false} placement="left">
        <PopoverTrigger>
          <Link alignItems="center" onClick={onClick}>
            <Icon as={SuccessIcon} color={colorStyle.color.success} pr="4px" />
            <Text pl="2px">{t('policy.table.scanInfo.view')}</Text>
          </Link>
        </PopoverTrigger>
        <PopoverContent PopperProps={{ usePortal: true }}>
          <Scrollbar maxHeight={160} overflow="scroll" width="270px">
            <Box>
              {item2 && (
                <>
                  {/*<ManualScanSecondHistory*/}
                  {/*  createTime={item2.createTime}*/}
                  {/*  lastUpdateTime={item2.lastupdateTime}*/}
                  {/*  status={item2.status}*/}
                  {/*/>*/}
                  <ManualScanFirstHistory
                    policyName={policy.policyName}
                    createTime={item2.createTime}
                    lastUpdateTime={item2.lastupdateTime}
                    status={item2.status}
                    downloadEnable={
                      !!(
                        item2?.downloadLinkId &&
                        !item2.downloadLinkId.includes('.pdf')
                      )
                    }
                    downloadLinkId={item2?.downloadLinkId}
                  />
                  <ManualScanReport
                    data={item2}
                    policyType={policyType}
                    showFailLink={scanHistory?.length === 1}
                    handleCheckSkippedModal={() => {
                      setModal({
                        name: PolicyListModalName.CheckSkipped,
                        policy,
                        scanHistory: item2,
                      });
                    }}
                  />
                  <br />
                </>
              )}
              <ManualScanFirstHistory
                policyName={policy.policyName}
                createTime={item.createTime}
                lastUpdateTime={item.lastupdateTime}
                scanMode={
                  (item.taskParameter ? JSON.parse(item.taskParameter) : {})
                    .scan_mode
                }
                status={item.status}
                downloadEnable={
                  !!(
                    item?.downloadLinkId &&
                    !item.downloadLinkId.includes('.pdf')
                  )
                }
                downloadLinkId={item?.downloadLinkId}
              />
              <ManualScanReport
                data={item}
                policyType={policyType}
                showFailLink={scanHistory.length === 1}
                handleCheckSkippedModal={() => {
                  setModal({
                    name: PolicyListModalName.CheckSkipped,
                    policy,
                    scanHistory: item,
                  });
                }}
              />
            </Box>
          </Scrollbar>
        </PopoverContent>
      </Popover>
    );
  }
};

const ManualScanStatusText = ({ status }: { status: string }) => {
  const { t } = useTranslation();

  if (!status) {
    return null;
  }
  if (checkManualScanCompleted(status)) {
    return (
      <Text>{`${t('policy.table.scanInfo.scanStatus.label')}: ${t(
        'policy.table.scanInfo.scanStatus.complete'
      )}`}</Text>
    );
  } else if (checkManualScanCanceled(status)) {
    return (
      <Text>{`${t('policy.table.scanInfo.scanStatus.label')}: ${t(
        'policy.table.scanInfo.scanStatus.stop'
      )}`}</Text>
    );
  } else if (checkManualScanFailed(status)) {
    return (
      <Text>{`${t('policy.table.scanInfo.scanStatus.label')}: ${t(
        'policy.table.scanInfo.scanStatus.fail'
      )}`}</Text>
    );
  }
  return null;
};

const ManualScanSeeDetailText = ({
  policyName,
  downloadLinkId,
}: {
  policyName: string;
  downloadLinkId: string;
}) => {
  const { offset } = useUtcOffsetStore();
  const { t } = useTranslation();
  const { widthReadablePermission } = usePermissionStore();
  const [Loading, setLoading] = useState(false);

  if (!widthReadablePermission(PermissionNameEnum.log) || !downloadLinkId) {
    return null;
  }

  return (
    <>
      {downloadLinkId && (
        <Flex align="center" direction="row">
          <Link
            onClick={async () => {
              setLoading(true);
              try {
                const res = await ReportApi.downloadReport(
                  downloadLinkId,
                  ConsoleType.NewConsole
                );
                if (res) {
                  const url = window.URL.createObjectURL(
                    new Blob([res as any], { type: 'application/pdf' })
                  );
                  const a = document.createElement('a');
                  a.href = url;
                  a.download =
                    policyName +
                    '-' +
                    dayjs().utcOffset(offset).format('YYYYMMDD') +
                    '.pdf';
                  a.click();
                  a.remove();
                }
              } catch (error: any) {
                const code = error?.data?.status || 'Unknown';
                window.showNotification({
                  titleMsg: t('reportCommon.alert.downloadReport.fail'),
                  bodyMsg:
                    [404004].indexOf(code) >= 0
                      ? t(`reportCommon.alert.downloadReport.failDesc.${code}`)
                      : t('reportCommon.alert.downloadReport.failDesc'),
                  severity: 'error',
                });
              } finally {
                setLoading(false);
              }
            }}
          >
            {t('policy.table.scanInfo.seeDetails')}
          </Link>
          {Loading && <Spinner ml="8px" size="xs" />}
        </Flex>
      )}
    </>
  );
};

const ManualScanFirstHistory = ({
  policyName,
  createTime,
  lastUpdateTime,
  scanMode,
  status,
  downloadEnable,
  downloadLinkId,
}: {
  policyName: string;
  createTime: string;
  lastUpdateTime: string;
  scanMode?: string | number;
  status: string;
  downloadEnable: boolean;
  downloadLinkId: string;
}) => {
  const timeModel = useUtcOffsetStore();
  const { t } = useTranslation();

  return (
    <>
      <Text>{`${t('policy.table.scanInfo.startAt')}: ${getUtcTime(
        createTime,
        timeModel.offset,
        t('common.momentDateFormat')
      )}`}</Text>
      {lastUpdateTime && (
        <Text>
          {`${t('policy.table.scanInfo.lastScan')}: ${getUtcTime(
            lastUpdateTime,
            timeModel.offset,
            t('common.momentDateFormat')
          )}`}
        </Text>
      )}
      {typeof scanMode !== 'undefined' && (
        <Text>
          {`${t('policy.table.scanInfo.scanType')}: ${
            scanMode === '1' || scanMode === 1
              ? t('policy.modals.manualScan.scanType.scanOnly')
              : t('policy.modals.manualScan.scanType.scanAndProtect')
          }`}
        </Text>
      )}
      <ManualScanStatusText status={status} />
      {downloadEnable && (
        <ManualScanSeeDetailText
          policyName={policyName}
          downloadLinkId={downloadLinkId}
        />
      )}
    </>
  );
};

const ManualScanReport = ({
  data,
  policyType,
  showFailLink,
  handleCheckSkippedModal,
}: {
  data: any;
  policyType: PolicyTypeEnum;
  showFailLink: boolean;
  handleCheckSkippedModal: () => void;
}) => {
  const { t } = useTranslation();
  const overAllPolicyType = useContext(OverAllPolicyTypeContext);
  const {
    countFailure,
    countAbort,
    countComplete,
    countTotal,
    scannedTotal,
    detectionSummary = {},
  } = data;
  const manualScanCategory = policyType2ManualScanCategory(policyType);

  const _showFailLink =
    showFailLink && countFailure > 0 && !checkManualScanCanceled(data.status);

  const isMailPolicy = [PolicyTypeEnum.Exchange, PolicyTypeEnum.Gmail].includes(
    policyType
  );

  let detectedAndBlockedContent: JSX.Element = <></>;

  if (overAllPolicyType === OverAllPolicyType.ATP) {
    const advancedSpamProtectionCount =
      detectionSummary['Advanced Spam Protection'] || 0;
    const malwareScanningCount = detectionSummary['Malware Scanning'] || 0;
    const fileBlockingCount = detectionSummary['File Blocking'] || 0;
    const webReputationCount = detectionSummary['Web Reputation'] || 0;
    // TODO: 如果有数字的话，增加链接跳转
    detectedAndBlockedContent = (
      <>
        {isMailPolicy && (
          <Text>
            {t('policy.table.scanInfo.spamEmailDetected', {
              NUMBER: advancedSpamProtectionCount,
            })}
          </Text>
        )}
        <Text>
          {t('policy.table.scanInfo.malwareDetected', {
            NUMBER: malwareScanningCount,
          })}
        </Text>
        <Text>
          {t('policy.table.scanInfo.filesBlocked', {
            NUMBER: fileBlockingCount,
          })}
        </Text>
        <Text>
          {t('policy.table.scanInfo.URLsDetected', {
            NUMBER: webReputationCount,
          })}
        </Text>
      </>
    );
  } else {
    const dataLossDetectedCount = detectionSummary['Data Loss Prevention'] || 0;
    const keywordExtractionCount = detectionSummary['Keyword Extraction'] || 0;

    detectedAndBlockedContent = (
      <>
        <Text>
          {t('policy.table.scanInfo.dataLossDetected', {
            NUMBER: dataLossDetectedCount,
          })}
        </Text>
        {keywordExtractionCount > 0 && (
          <Text>
            {t('policy.table.scanInfo.keywordExtraction', {
              NUMBER: keywordExtractionCount,
            })}
          </Text>
        )}
      </>
    );
  }

  return (
    <>
      <Text>
        {t(`policy.table.scanInfo.${manualScanCategory}.scanned`, {
          NUMBER: countComplete,
          TOTAL: countTotal,
        })}
      </Text>
      <Text>
        {t(`policy.table.scanInfo.${manualScanCategory}.skipped`, {
          NUMBER: countFailure + countAbort,
          TOTAL: countTotal,
        })}
      </Text>
      <Text>
        {t(`policy.table.scanInfo.${isMailPolicy ? 'email' : 'file'}.scanned`, {
          TOTAL: scannedTotal,
        })}
      </Text>
      <br />
      {detectedAndBlockedContent}
      {/* TODO */}
      {_showFailLink && (
        <Link onClick={handleCheckSkippedModal}>
          {t(`policy.table.scanInfo.${manualScanCategory}.checkSkipped`)}
        </Link>
      )}
    </>
  );
};

export function policyType2ManualScanCategory(policyType?: PolicyTypeEnum) {
  let manualScanCategory = '';
  switch (policyType) {
    case PolicyTypeEnum.Exchange:
    case PolicyTypeEnum.Gmail:
      manualScanCategory = 'mailbox';
      break;
    case PolicyTypeEnum.SharePoint:
      manualScanCategory = 'site';
      break;
    case PolicyTypeEnum.Teams:
      manualScanCategory = 'site';
      break;
    default:
      manualScanCategory = 'user';
      break;
  }
  return manualScanCategory;
}
